package top.wcpe.allincore.ui.runtime.internal.concurrent

/**
 * 并发原语的平台中立契约（FR-UI-021 reactive 迁 commonMain 的 expect 面）。
 *
 * JVM actual 委托 `java.util.concurrent`（与迁移前语义逐一等价）；
 * JS actual 因单线程事件循环无真并发，退化为平凡实现（无锁、普通变量）。
 * `internal` 仅限 ui-runtime 模块内部使用，不进对外 ABI。
 */

/**
 * 互斥锁：替代原 `@Synchronized` / `synchronized(this)`。
 *
 * JVM actual = 可重入锁（与原 monitor 同为可重入互斥语义）；JS actual = 空操作。
 * 原代码的 `@Synchronized` 方法迁移后改为「类内私有 SyncLock 字段 + [withLock] 块」，
 * 同类各方法共享同一把锁——与原「同一 this 监视器」语义一致（模块外无人持有这些监视器）。
 */
internal expect class SyncLock() {
    fun acquire()

    fun release()
}

/** [SyncLock] 的作用域用法；common 内定义，双端共享。 */
internal inline fun <R> SyncLock.withLock(block: () -> R): R {
    acquire()
    try {
        return block()
    } finally {
        release()
    }
}

/**
 * 线程局部引用：替代 `java.lang.ThreadLocal`。
 *
 * JVM actual = `ThreadLocal.withInitial`；JS actual = 单线程下的普通容器（惰性初始化）。
 */
internal expect class ThreadLocalRef<T>(initial: () -> T) {
    fun get(): T

    /** 覆写当前线程的值（FR-UI-021 批E PropApplyContext 迁移新增调用方）。 */
    fun set(value: T)
}

/**
 * 身份相等（`===`）的可变 Set：替代 `Collections.newSetFromMap(IdentityHashMap())`。
 *
 * 订阅者/依赖集按引用身份去重（订阅者对象可能覆写 equals，身份语义不能依赖 equals）。
 * JVM actual = IdentityHashMap 背书；JS actual = ArrayList + `===` 线性实现
 * （订阅者集合规模小，线性代价可接受）。
 */
internal expect fun <T> newIdentitySet(): MutableSet<T>

/**
 * 身份相等（`===`）的可变 Map：替代 `java.util.IdentityHashMap`（按引用身份索引，不依赖 equals）。
 *
 * JVM actual = IdentityHashMap；JS actual = 普通 HashMap（键为无 equals 覆写的对象时天然身份语义，
 * 如 UIElement；调用方须保证键类型不覆写 equals/hashCode）。
 */
internal expect fun <K : Any, V> newIdentityMap(): MutableMap<K, V>

/**
 * 线程安全的身份 Map：替代 `Collections.synchronizedMap(IdentityHashMap())`。
 * JVM actual 逐字面等价；JS actual = 普通 HashMap（单线程，且 UIElement 无 equals 覆写，身份即相等）。
 */
internal expect fun <K : Any, V> newConcurrentIdentityMap(): MutableMap<K, V>

/**
 * 阻塞当前线程 [millis] 毫秒：替代 `Thread.sleep`（仅状态机同步测试模式使用）。
 * JVM actual = Thread.sleep（中断时恢复中断位后返回）；JS actual 无同步阻塞能力，显式失败——
 * JS 端 EvalContext 恒有 executor（PlatformScheduler），不会落入同步回退路径。
 */
internal expect fun blockingDelayForTest(millis: Long)

/**
 * 并发安全的可变 Set：替代 `ConcurrentHashMap.newKeySet()`。
 * JVM actual = `ConcurrentHashMap.newKeySet()`；JS actual = 单线程下的 LinkedHashSet。
 * 注意：遍历期间可能移除元素时（如 dispose 回调内自移除），调用方须先 `toList()` 快照再遍历。
 */
internal expect fun <T> newConcurrentSet(): MutableSet<T>

/**
 * 原子布尔标志：替代 `java.util.concurrent.atomic.AtomicBoolean`（取消/完成标志场景）。
 * JVM actual = AtomicBoolean；JS actual = 普通 var（单线程天然原子）。
 */
internal expect class AtomicBooleanFlag(initial: Boolean) {
    fun get(): Boolean

    fun set(value: Boolean)

    /** 当前值等于 [expected] 时原子置为 [update] 并返回 true；否则返回 false。 */
    fun compareAndSet(expected: Boolean, update: Boolean): Boolean
}

/**
 * 支持原子 putIfAbsent / computeIfAbsent 的并发 Map：替代 `java.util.concurrent.ConcurrentHashMap`。
 *
 * JVM actual = ConcurrentHashMap（读路径不持锁）；JS actual = 普通 HashMap（单线程天然原子）。
 * 迭代经 [entriesSnapshot] 快照副本——比 CHM 的弱一致迭代语义更强，调用方遍历期间可安全增删。
 */
internal expect class ConcurrentMutableMap<K : Any, V : Any>() {
    /** 键不存在时放入并返回 null；已存在则返回既有值且不覆盖（原子）。 */
    fun putIfAbsent(key: K, value: V): V?

    /** 键不存在时以 [compute] 计算并放入（JVM 上原子、compute 至多一次生效），返回最终值。 */
    fun computeIfAbsent(key: K, compute: (K) -> V): V

    operator fun get(key: K): V?

    operator fun set(key: K, value: V)

    /** 移除并返回旧值（无则 null）。 */
    fun remove(key: K): V?

    /** 仅当 [key] 当前映射值等于 [expected] 时移除（JVM 上原子）；返回是否发生移除。 */
    fun remove(key: K, expected: V): Boolean

    /** 清空所有条目。 */
    fun clear()

    fun size(): Int

    /** 当前键值对的快照副本（保序不保证；遍历期间对本 Map 的增删不影响快照）。 */
    fun entriesSnapshot(): List<Pair<K, V>>
}

/**
 * 泛型原子引用：替代 `AtomicReference`（SPI Holder 三件套的并发基座）。
 * JS actual = 普通 var。仅暴露 get/set（现有 Holder 只用这两个，不预留 CAS）。
 */
internal expect class AtomicRef<T>(initial: T) {
    fun get(): T

    fun set(value: T)
}

/**
 * 迭代安全的并发 List：替代 `CopyOnWriteArrayList`（回调列表场景——遍历期间可增删）。
 * JVM actual = CopyOnWriteArrayList；JS actual = 普通 ArrayList（单线程且调用方遍历前自行快照）。
 */
internal expect fun <T> newConcurrentList(): MutableList<T>

/** 原子 Int 计数器：替代 `AtomicInteger`。JS actual = 普通 var。 */
internal expect class AtomicIntCounter(initial: Int) {
    fun get(): Int

    fun set(value: Int)

    fun incrementAndGet(): Int

    fun decrementAndGet(): Int
}

/** 原子 Long 计数器：替代 `AtomicLong`。JS actual = 普通 var。 */
internal expect class AtomicLongCounter(initial: Long) {
    fun get(): Long

    fun set(value: Long)

    /** CAS（FR-UI-021 批F IrSession.lastTickAtMs 迁移新增调用方）。 */
    fun compareAndSet(expected: Long, update: Long): Boolean

    fun incrementAndGet(): Long
}

/**
 * 弱键 Map：替代 `java.util.WeakHashMap`（键被 GC 后条目自动消失）。
 *
 * JVM actual = WeakHashMap；JS actual = 普通 HashMap（common Kotlin 无可迭代弱引用容器）——
 * JS 侧条目依赖调用方的显式清理路径（如 RippleAnimator 过期清理 / clearAll）兜底，
 * 泄漏面有界（单元素条目随动画过期即删）。
 */
internal expect fun <K : Any, V : Any> newWeakKeyedMap(): MutableMap<K, V>
